import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Star, Instagram } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative flex items-center pt-24 pb-14 lg:pt-20 lg:pb-0 lg:min-h-screen overflow-hidden bg-gradient-hero"
    >
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-48 h-48 sm:w-72 sm:h-72 bg-blue-200 rounded-full blur-3xl opacity-40" />
      <div className="absolute bottom-20 left-10 w-48 h-48 sm:w-96 sm:h-96 bg-orange-200 rounded-full blur-3xl opacity-30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-100 rounded-full blur-3xl opacity-20 hidden lg:block" />
      
      {/* Dots Pattern */}
      <div className="absolute inset-0 dots-pattern" />

      {/* Google Reviews Badge - Top Right (Desktop only) */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="absolute top-24 right-4 md:right-10 z-20 hidden md:block"
      >
        <a 
          href="https://www.google.com/search?q=Celine+Coutand+Tutoring+Grosrouvre"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-white shadow-lg rounded-full px-4 py-2 hover:shadow-xl transition-shadow border border-gray-100"
        >
          <div className="flex">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          </div>
          <span className="text-sm font-medium text-blue-700">5.0 - 16 avis Google</span>
        </a>
      </motion.div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center lg:text-left"
          >

            {/* Google Reviews Badge - Mobile inline */}
            <a
              href="https://www.google.com/search?q=Celine+Coutand+Tutoring+Grosrouvre"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-badge-mobile"
              className="md:hidden inline-flex items-center gap-2 bg-white shadow-md rounded-full px-3 py-1.5 border border-blue-100 mb-5"
            >
              <div className="flex">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={`m-star-${i}`} className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <span className="text-xs font-semibold text-blue-700">5.0 · 16 avis Google</span>
            </a>

            {/* Main Heading */}
            <h1 className="text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight font-bold mb-5 md:mb-6 text-blue-900">
              Révélez le{" "}
              <span className="text-gradient">potentiel</span>
              <br />
              de vos enfants
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-base sm:text-lg md:text-xl text-blue-800/70 mb-6 md:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed"
            >
              Cours particuliers, cours collectifs et stages de révisions pour accompagner 
              chaque élève vers la réussite scolaire.
            </motion.p>

            {/* Key Points */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-x-4 gap-y-2 mb-7 md:mb-8 justify-center lg:justify-start"
            >
              {["Enseignants qualifiés", "Suivi personnalisé", "Résultats garantis"].map((point) => (
                <div key={point} className="flex items-center gap-2 text-xs sm:text-sm text-blue-800/80">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                  <span>{point}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="btn-primary inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-500 hover:from-orange-600 hover:via-orange-500 hover:to-yellow-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                <span>Demander un devis gratuit</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="btn-blue inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                <span>Découvrir nos services</span>
              </a>
            </motion.div>

            {/* English Class Promo Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65 }}
              className="mt-6"
            >
              <a 
                href="/english-class"
                data-testid="hero-english-class-banner"
                className="flex w-full lg:w-auto lg:inline-flex items-center gap-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white px-4 sm:px-5 py-3 rounded-2xl shadow-lg hover:scale-105 transition-all group relative overflow-hidden"
              >
                {/* Shine effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                
                <div className="relative z-10 flex items-center gap-3 w-full">
                  {/* UK Flag Emoji */}
                  <span className="text-2xl flex-shrink-0">🇬🇧</span>
                  
                  <div className="text-left flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-bold text-sm sm:text-base">English Class</span>
                      <span className="bg-yellow-400 text-yellow-900 text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                        INSCRIPTIONS OUVERTES
                      </span>
                    </div>
                    <div className="text-[11px] sm:text-xs text-white/90">
                      Cours ludiques dès 3 ans jusqu'au CM2
                    </div>
                  </div>
                  
                  <ArrowRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            </motion.div>

            {/* Mobile Stage Promo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="lg:hidden mt-6"
            >
              <button 
                data-tally-open="81rrAr"
                data-tally-layout="modal"
                data-tally-width="500"
                data-tally-auto-close="3000"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-2xl shadow-xl hover:scale-[1.02] transition-transform relative overflow-hidden group text-left"
              >
                {/* Animated shine effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                
                {/* Pulsing glow */}
                <div className="absolute inset-0 rounded-2xl animate-pulse-glow" />
                
                <div className="flex items-center gap-3 relative z-10">
                  <div className="bg-white rounded-full p-2 flex-shrink-0 animate-bounce-subtle">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-bold uppercase tracking-wider text-pink-200 flex items-center gap-2">
                      <span>Stages Toussaint</span>
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400"></span>
                      </span>
                    </div>
                    <div className="text-base font-bold">
                      Inscrivez-vous dès maintenant !
                    </div>
                    <div className="text-xs text-white/90">
                      2h par jour • Présentiel ou visio
                    </div>
                  </div>
                  <div className="bg-white text-purple-600 px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 animate-pulse-button">
                    Réserver
                  </div>
                </div>
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-10 md:mt-12"
            >
              <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:flex lg:flex-wrap lg:gap-6 lg:items-center">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">1000+</div>
                  <div className="text-[11px] sm:text-sm text-blue-800/60">Élèves accompagnés</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">15+</div>
                  <div className="text-[11px] sm:text-sm text-blue-800/60">Années d'expérience</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">2</div>
                  <div className="text-[11px] sm:text-sm text-blue-800/60">Centres</div>
                </div>

                {/* Instagram Badge */}
                <a
                  href="https://www.instagram.com/celinepotentielplus/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-instagram-badge"
                  className="col-span-3 lg:col-span-1 flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-4 py-2.5 rounded-full hover:scale-105 transition-transform shadow-lg mt-4 lg:mt-0"
                >
                  <Instagram className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-sm font-bold">6.5K+</div>
                    <div className="text-xs opacity-90">Suivez-nous</div>
                  </div>
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src="https://images.pexels.com/photos/8466702/pexels-photo-8466702.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Soutien scolaire personnalisé"
                  className="w-full h-[500px] object-cover"
                />
                
                {/* Stage Promo Overlay - OPTION D: Violet/Magenta avec animations */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, duration: 0.5, type: "spring" }}
                  className="absolute bottom-4 left-4 right-4"
                >
                  <button 
                    data-tally-open="81rrAr"
                    data-tally-layout="modal"
                    data-tally-width="500"
                    data-tally-auto-close="3000"
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-2xl shadow-2xl hover:scale-[1.02] transition-transform cursor-pointer relative overflow-hidden group text-left"
                  >
                    {/* Animated shine effect */}
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    
                    {/* Pulsing glow effect */}
                    <div className="absolute inset-0 rounded-2xl animate-pulse-glow" />
                    
                    <div className="flex items-center gap-3 relative z-10">
                      {/* Animated icon with bounce */}
                      <div className="bg-white rounded-full p-2 flex-shrink-0 animate-bounce-subtle">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-bold uppercase tracking-wider text-pink-200 mb-0.5 flex items-center gap-2">
                          <span>Stages Toussaint</span>
                          {/* Blinking dot */}
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400"></span>
                          </span>
                        </div>
                        <div className="text-lg font-bold">
                          Inscrivez-vous dès maintenant !
                        </div>
                        <div className="text-sm text-white/90">
                          2h par jour • Présentiel ou visio
                        </div>
                      </div>
                      {/* Animated button */}
                      <div className="bg-white text-purple-600 px-3 py-1.5 rounded-full text-sm font-bold flex-shrink-0 animate-pulse-button shadow-lg">
                        Réserver
                      </div>
                    </div>
                  </button>
                </motion.div>
              </div>

            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
